import React, { useEffect, useState, useMemo } from "react";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { Dialog,DialogTitle,DialogContent,DialogActions } from "@mui/material";
import { Snackbar, Alert } from "@mui/material";
import { useSnackbar } from "../../utils/SnackbarProvider";
import LockIcon from '@mui/icons-material/Lock';
import { Chip } from "@mui/material";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  InputAdornment,
  LinearProgress,
  Divider,
  Avatar,
} from "@mui/material";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import VendorLogoUploader from "./VendorLogoUploader";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MapPicker from "./MapPicker";
/* ============================
   ZOD SCHEMA (SAME PATTERN)
   ============================ */
const VendorSchema = z
  .object({
    name: z.string().min(3, "Name must be at least 3 characters"),
    email: z.string().email("Invalid email"),
    phone: z.string().min(10, "Invalid phone number"),

    password: z.string().optional(),
    confirmPassword: z.string().optional(),

    uscc: z
      .string()
      .length(18, "USCC must be exactly 18 characters")
      .regex(/^[0-9A-Z]+$/, "Only digits & capital letters"),

    bankAccountName: z.string().min(2),
    bankAccountNumber: z.string().min(6),
    bankName: z.string().min(2),
    bankBranch: z.string().min(2),
  //   specializations: z
  // .array(z.string())
  // .max(3, "Max 3 specializations")
  // .optional(),
    businessName: z.string().min(2, "Business name required").optional(),
    tagline: z.string().optional(),
    description: z.string().optional(),
    addressLine1: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    pincode: z.string().optional(),
    latitude: z.any().optional(),
    longitude: z.any().optional(),

    aboutBusinessStory: z.string().optional(),
    aboutMission: z.string().optional(),
    aboutValues: z.string().optional(),
    yearsOfExperience: z.string().optional(),

  })
  .refine(
    (data) =>
      !data.password || data.password === data.confirmPassword,
    {
      message: "Passwords do not match",
      path: ["confirmPassword"],
    }
  );

/* ============================
   PASSWORD STRENGTH
   ============================ */
const getPasswordStrength = (password) => {
  let score = 0;
  if (!password) return { score: 0, color: "#d32f2f" };
  if (password.length >= 6) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[\W_]/.test(password)) score++;
  return { score, color: score >= 4 ? "#2e7d32" : "#f9a825" };
};

const SPECIALIZATION_OPTIONS = [
  "Electronics",
  "Fashion",
  "Home Decor",
  "Grocery",
  "Beauty",
  "Sports",
  "Books",
  "Toys",
];

const VendorRegistration = () => {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0 });
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirmNew, setShowConfirmNew] = useState(false);
const [snackbarOpen, setSnackbarOpen] = useState(false);

const [vendorLogo, setVendorLogo] = useState("");
const [logoFile, setLogoFile] = useState(null);
const fileInputRef = useRef(null);

const [phoneVerified, setPhoneVerified] = useState(false);
const [otpSent, setOtpSent] = useState(false);
const [otpCode, setOtpCode] = useState("");
const [otpLoading, setOtpLoading] = useState(false);
const [otpTimer, setOtpTimer] = useState(0);
const [bannerFiles, setBannerFiles] = useState([]);
const [storeImages, setStoreImages] = useState([]);
const [selectedSpecializations, setSelectedSpecializations] = useState([]);
const [specializationOptions, setSpecializationOptions] = useState([]);
const { showSnackbar } = useSnackbar();

  const PasswordSchema = z.object({
  currentPassword: z.string().min(6),
  newPassword: z.string().min(6),
  confirmNewPassword: z.string().min(6),
    }).refine(data => data.newPassword === data.confirmNewPassword, {
      message: "Passwords do not match",
      path: ["confirmNewPassword"],
    });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(VendorSchema),
    mode: "onBlur",
  });

const {
  register: registerPassword,
  handleSubmit: handlePasswordSubmit,
  watch: watchPassword,
  reset: resetPasswordForm,
  formState: { errors: passwordErrors },
} = useForm({
  resolver: zodResolver(PasswordSchema),
  mode: "onBlur",
});

const initialLocation = useMemo(() => ({
  latitude: watch("latitude"),
  longitude: watch("longitude"),
}), [watch("latitude"), watch("longitude")]);
useEffect(() => {
  let interval;

  if (otpTimer > 0) {
    interval = setInterval(() => {
      setOtpTimer((prev) => prev - 1);
    }, 1000);
  }

  return () => clearInterval(interval);
}, [otpTimer]);

useEffect(() => {
  const loadSpecializations = async () => {
    try {
      const res = await api.get("/specializations");
      setSpecializationOptions(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  loadSpecializations();
}, []);

  /* ============================
     LOAD EXISTING VENDOR PROFILE
     ============================ */
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await api.get("/vendor/profile");

        const { user, vendorProfile } = res.data;
        setVendorLogo(vendorProfile?.profileImageUrl || "");
        reset({
  name: vendorProfile?.name || "",
  email: user?.email || "",
  phone: user?.mobile || "",
  uscc: vendorProfile?.uscc || "",

  businessName: vendorProfile?.store?.businessName || "",
  tagline: vendorProfile?.store?.tagline || "",
  description: vendorProfile?.store?.description || "",

  addressLine1: vendorProfile?.store?.address?.addressLine1 || "",
  city: vendorProfile?.store?.address?.city || "",
  state: vendorProfile?.store?.address?.state || "",
  pincode: vendorProfile?.store?.address?.pincode || "",
  latitude: vendorProfile?.store?.location?.latitude || "",
  longitude: vendorProfile?.store?.location?.longitude || "",

  bankAccountName: vendorProfile?.bankDetails?.accountName || "",
  bankAccountNumber: vendorProfile?.bankDetails?.accountNumber || "",
  bankName: vendorProfile?.bankDetails?.bankName || "",
  bankBranch: vendorProfile?.bankDetails?.bankBranch || "",
  specializations: vendorProfile?.specializations || [],
  aboutBusinessStory: vendorProfile?.store?.about?.businessStory || "",
  aboutMission: vendorProfile?.store?.about?.mission || "",
  aboutValues: vendorProfile?.store?.about?.values?.join(", ") || "",
  yearsOfExperience: vendorProfile?.store?.about?.yearsOfExperience || "",
  password: "",
  confirmPassword: "",
});
      setSelectedSpecializations(
  vendorProfile?.specializations?.map((s) =>
    typeof s === "object" ? String(s._id) : String(s)
  ) || []
);
        setNeedsPassword(false);
        setPhoneVerified(user?.mobileVerified || false);
      } catch (err) {
  if (err.response?.status === 401) {
    const localUser = JSON.parse(localStorage.getItem("user"));

    reset({
      name: "",
      email: localUser?.username || "",
      phone: "",
      uscc: "",
      bankAccountName: "",
      bankAccountNumber: "",
      bankName: "",
      bankBranch: "",
      password: "",
      confirmPassword: "",
    });

    setNeedsPassword(true);
  } else {
    console.error(err);
  }
}
    };

    loadProfile();
  }, [reset]);

  /* ============================
     PASSWORD STRENGTH WATCH
     ============================ */
  const passwordValue = watch("password");
  useEffect(() => {
    setPasswordStrength(getPasswordStrength(passwordValue));
  }, [passwordValue]);


  const handleSendOTP = async () => {
  try {
    const phone = watch("phone");

    if (!phone) {
      showSnackbar("Enter phone number first", "error");
      return;
    }

    setOtpLoading(true);

    const res = await api.post("/users/send-otp-whatsapp", {
      phoneNumber: phone,
    });

    if (res.data.success) {
      setOtpSent(true);
      setOtpTimer(30);
      // alert("OTP sent to your WhatsApp");
      showSnackbar("OTP sent to your WhatsApp", "success");
    }

  } catch (err) {
    // alert(err.response?.data?.message || "Failed to send OTP");
    showSnackbar(err.response?.data?.message || "Failed to send OTP", "error");
  } finally {
    setOtpLoading(false);
  }
};

const handleVerifyOTP = async () => {
  try {
    const phone = watch("phone");

    const res = await api.post("/users/verify-otp", {
      phoneNumber: phone,
      otpCode,
    });

    if (res.data.success) {
      // console.log("Phone verified:", res.data);
      setPhoneVerified(res.data.user.mobileVerified || false);
      setOtpSent(false);
      // alert("Phone verified successfully");
      showSnackbar("Phone verified successfully", "success");
    }

  } catch (err) {
    // alert(err.response?.data?.message || "Invalid OTP");
    showSnackbar(err.response?.data?.message || "Invalid OTP", "error");
  }
};

// const handleSpecializationChange = (value) => {
//   setSelectedSpecializations((prev) => {
//     if (prev.includes(value)) {
//       return prev.filter((item) => item !== value);
//     }

//     if (prev.length >= 3) {
//       showSnackbar("Maximum 3 specializations allowed", "warning");
//       return prev;
//     }

//     return [...prev, value];
//   });
// };

const handleSpecializationToggle = (name) => {
  setSelectedSpecializations((prev) => {
    if (prev.includes(name)) {
      return prev.filter((i) => i !== name);
    }

    if (prev.length >= 3) {
      showSnackbar("You can select max 3", "warning");
      return prev;
    }

    return [...prev, name];
  });
};
  /* ============================
     SUBMIT HANDLER
     ============================ */
  const onSubmit = async (data) => {
    // console.log("Submit handler")
      if (!phoneVerified) {
    // alert("Please verify phone number first");
    showSnackbar("Please verify phone number first", "error");
    return;
  }
    try {
      const user = JSON.parse(localStorage.getItem("user"));

      /* 🔐 STEP 1: SET PASSWORD (ONLY ONCE) */
      if (needsPassword && data.password) {
        const res = await api.post("/auth/set-password", {
          username: user.username,
          password: data.password,
        });

        // 🔥 STORE TOKEN
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("user", JSON.stringify(res.data.user));
      }

      /* 🏢 STEP 2: SAVE / UPDATE VENDOR PROFILE */
const formData = new FormData();

// BASIC
formData.append("name", data.name);
formData.append("email", data.email);
formData.append("phone", data.phone);
formData.append("uscc", data.uscc);

// COMPANY
formData.append(
  "specializations",
  JSON.stringify(selectedSpecializations)
);
formData.append("businessName", data.businessName || "");
formData.append("tagline", data.tagline || "");
formData.append("description", data.description || "");

const aboutPayload = {
  businessStory: data.aboutBusinessStory || "",
  mission: data.aboutMission || "",
  values: data.aboutValues
    ? data.aboutValues.split(",").map((v) => v.trim())
    : [],
  yearsOfExperience: data.yearsOfExperience
    ? Number(data.yearsOfExperience)
    : null,
};

formData.append("about", JSON.stringify(aboutPayload));
// ADDRESS
formData.append("addressLine1", data.addressLine1 || "");
formData.append("city", data.city || "");
formData.append("state", data.state || "");
formData.append("pincode", data.pincode || "");
formData.append("latitude", data.latitude || "");
formData.append("longitude", data.longitude || "");

// BANK
formData.append("bankAccountName", data.bankAccountName);
formData.append("bankAccountNumber", data.bankAccountNumber);
formData.append("bankName", data.bankName);
formData.append("bankBranch", data.bankBranch);

// FILES
if (logoFile) formData.append("logo", logoFile);

bannerFiles.forEach((file) => {
  formData.append("banner", file);
});

storeImages.forEach((img) => {
  formData.append("storeImages", img);
});

await api.put("/vendor/profile", formData, {
  headers: { "Content-Type": "multipart/form-data" },
});

      navigate("/vendor/products");
    } catch (err) {
      // alert(err.response?.data?.message || "Failed to save vendor profile");
      showSnackbar(err.response?.data?.message || "Failed to save vendor profile", "error");
    }
  };

const handlePasswordChange = async (data) => {
  try {
    await api.put("/auth/change-password", {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });

    showSnackbar("Password updated successfully", "success");

    resetPasswordForm();
    setOpenPasswordDialog(false);

  } catch (err) {
    showSnackbar(
      err.response?.data?.message || "Failed to update password",
      "error"
    );
  }
};

const getInitials = (name = "") => {
  if (!name) return "na";

  const words = name.trim().split(" ");

  if (words.length === 1) return words[0][0].toUpperCase();

  return (words[0][0] + words[1][0]).toUpperCase();
};

const getAvatarColor = (name = "") => {
  const colors = [
    "#1976d2",
    "#2e7d32",
    "#ed6c02",
    "#9c27b0",
    "#0288d1",
  ];

  if (!name) return colors[0];

  let hash = 0;

  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

const handleLogoUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const preview = URL.createObjectURL(file);
  setVendorLogo(preview); // instant preview

  const formData = new FormData();
  formData.append("logo", file);

  // try {
  //   const res = await api.post("/vendor/upload-logo", formData, {
  //     headers: { "Content-Type": "multipart/form-data" },
  //   });

  //   setVendorLogo(res.data.profileImageUrl);
  // } catch (err) {
  //   console.error("Upload failed", err);
  // }
};
  return (
    <Box sx={{ minHeight: "100vh", pt: 8, bgcolor: "#f5f5f5" }}>
      <Paper sx={{ p: 4, maxWidth: 450, mx: "auto" }}>
        <Typography variant="h6" fontWeight={600} mb={3}>
          Vendor Profile
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box textAlign="center" mb={2}>
 <VendorLogoUploader
  vendorLogo={vendorLogo}
  setVendorLogo={setVendorLogo}
  setLogoFile={setLogoFile}
  vendorName={watch("name")}
/>
  <input
    type="file"
    hidden
    ref={fileInputRef}
    accept="image/*"
    onChange={handleLogoUpload}
  />
</Box>
<IconButton
  onClick={() => setOpenPasswordDialog(true)}
  size="small"
>
  <LockIcon />
</IconButton>
          <TextField InputLabelProps={{ shrink: true }} fullWidth label="Name" {...register("name")} error={!!errors.name} helperText={errors.name?.message} margin="normal" />
          <TextField InputLabelProps={{ shrink: true }} fullWidth label="Email" {...register("email")} error={!!errors.email} helperText={errors.email?.message} margin="normal" />
          
          <Box display="flex" gap={1} alignItems="center">
<Box mt={1}>
  <Typography fontWeight={500} mb={1}>
    Phone Number
  </Typography>

  <Box display="flex" gap={1} alignItems="center">

    <TextField
      fullWidth
      disabled={phoneVerified}
      {...register("phone")}
      error={!!errors.phone}
      helperText={errors.phone?.message}
    />

    {!phoneVerified && (
      <Button
        variant="outlined"
        onClick={handleSendOTP}
        disabled={otpLoading || otpTimer > 0}
      >
        {otpTimer > 0 ? `Resend ${otpTimer}s` : "Verify"}
      </Button>
    )}

    {phoneVerified && (
      <CheckCircleIcon sx={{ color: "green", fontSize: 30 }} />
    )}
  </Box>
</Box>
{otpSent && !phoneVerified && (

<Box mt={2}>
  <Typography variant="body2" mb={1}>
    Enter OTP sent to WhatsApp
  </Typography>

  <Box display="flex" gap={2}>

    <TextField
      value={otpCode}
      onChange={(e) => setOtpCode(e.target.value)}
      placeholder="4 digit OTP"
      inputProps={{ maxLength: 4 }}
    />

    <Button
      variant="contained"
      onClick={handleVerifyOTP}
      sx={{
        bgcolor: "#ff9900",
        fontWeight: 600
      }}
    >
      Verify OTP
    </Button>

  </Box>
</Box>

)}

</Box>
          {needsPassword && (
            <>
              <TextField 
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                {...register("password")}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <LinearProgress
                variant="determinate"
                value={(passwordStrength.score / 5) * 100}
                sx={{ height: 6, borderRadius: 1, my: 1 }}
              />

              <TextField InputLabelProps={{ shrink: true }}
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                {...register("confirmPassword")}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}

<Divider sx={{ my: 3 }} />

<Typography variant="subtitle1" fontWeight={600}>
  Company Details
</Typography>

<Box mt={2}>
  {/* <Typography fontWeight={600} mb={1}>
    Specializations (Select up to 3)
  </Typography> */}

<Box mt={2}>
  <Typography fontWeight={400} mb={1}>
    Specializations (Max 3)
  </Typography>

  <Box display="flex" flexWrap="wrap" gap={1}>
    {specializationOptions.map((item) => {
      const selected = selectedSpecializations.includes(item.name);

      return (
        <Chip
          key={item._id}
          label={item.name}
          clickable
          color={selected ? "default" : "default"}
          variant={selected ? "filled" : "outlined"}
          onClick={() => handleSpecializationToggle(item.name)}
          sx={{
            fontWeight: 500,
            borderRadius: 2,
            ...(selected && {
     bgcolor: "#ff9900",
              color: "#000",
            }),
          }}
        />
      );
    })}
  </Box>
</Box>
</Box>
<Divider sx={{ my: 3 }} />
<TextField
  InputLabelProps={{ shrink: true }}
  fullWidth
  label="Business Name"
  {...register("businessName")}
  margin="normal"
/>

<TextField
InputLabelProps={{ shrink: true }}
  fullWidth
  label="Tagline"
  {...register("tagline")}
  margin="normal"
/>

<TextField
InputLabelProps={{ shrink: true }}
  fullWidth
  label="About Company"
  {...register("description")}
  margin="normal"
  multiline
  rows={3}
/>

<Divider sx={{ my: 3 }} />

<Typography variant="subtitle1" fontWeight={600}>
  About Your Business
</Typography>

<TextField
InputLabelProps={{ shrink: true }}
  fullWidth
  label="Business Story"
  {...register("aboutBusinessStory")}
  margin="normal"
  multiline
  rows={3}
/>

<TextField
InputLabelProps={{ shrink: true }}
  fullWidth
  label="Mission"
  {...register("aboutMission")}
  margin="normal"
  multiline
  rows={2}
/>

<TextField
InputLabelProps={{ shrink: true }}
  fullWidth
  label="Values (comma separated)"
  {...register("aboutValues")}
  margin="normal"
/>

<TextField
InputLabelProps={{ shrink: true }}
  fullWidth
  label="Years of Experience"
  {...register("yearsOfExperience")}
  margin="normal"
/>

<Box mt={2}>
  <Typography fontWeight={500}>Store Banner</Typography>
  <input
    type="file"
    multiple
    accept="image/*"
    onChange={(e) => {
      const files = Array.from(e.target.files);
      setBannerFiles((prev) => [...prev, ...files]);
    }}
  />
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
    {bannerFiles.map((file, index) => (
      <Chip
        key={`banner-${index}`}
        label={file.name}
        onDelete={() => setBannerFiles(prev => prev.filter((_, i) => i !== index))}
        size="small"
      />
    ))}
  </Box>
</Box>

<Box mt={2}>
  <Typography fontWeight={500}>Store Images</Typography>
  <input
    type="file"
    multiple
    accept="image/*"
    onChange={(e) => {
      const files = Array.from(e.target.files);
      setStoreImages((prev) => [...prev, ...files]);
    }}
  />
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
    {storeImages.map((file, index) => (
      <Chip
        key={`store-${index}`}
        label={file.name}
        onDelete={() => setStoreImages(prev => prev.filter((_, i) => i !== index))}
        size="small"
      />
    ))}
  </Box>
</Box>

 <Typography variant="subtitle1" fontWeight={600}>
  Business Address
</Typography>


<MapPicker
  setValue={setValue}
  initialLocation={initialLocation}
/>

<TextField
InputLabelProps={{ shrink: true }}
  fullWidth
  label="Address"
  {...register("addressLine1")}
  margin="normal"
/>

<TextField
InputLabelProps={{ shrink: true }}
  fullWidth
  label="City"
  {...register("city")}
  margin="normal"
/>

<TextField
InputLabelProps={{ shrink: true }}
  fullWidth
  label="State"
  {...register("state")}
  margin="normal"
/>

<TextField
InputLabelProps={{ shrink: true }}
  fullWidth
  label="Pincode"
  {...register("pincode")}
  margin="normal"
/>
<input type="hidden" {...register("latitude")} />
<input type="hidden" {...register("longitude")} /> 
          <TextField InputLabelProps={{ shrink: true }} fullWidth label="USCC" {...register("uscc")} error={!!errors.uscc} helperText={errors.uscc?.message} margin="normal" />

          <Divider sx={{ my: 3 }} />

          <TextField InputLabelProps={{ shrink: true }} fullWidth label="Account Holder Name" {...register("bankAccountName")} margin="normal" />
          <TextField InputLabelProps={{ shrink: true }} fullWidth label="Account Number" {...register("bankAccountNumber")} margin="normal" />
          <TextField InputLabelProps={{ shrink: true }} fullWidth label="Bank Name" {...register("bankName")} margin="normal" />
          <TextField InputLabelProps={{ shrink: true }} fullWidth label="Bank Branch" {...register("bankBranch")} margin="normal" />

          <Button
            type="submit"
            fullWidth
            disabled={isSubmitting}
            variant="contained"
            sx={{ mt: 3, bgcolor: "#ff9900", fontWeight: 600 }}
          >
            Save Vendor Profile
          </Button>
        </form>
        <Dialog
  open={openPasswordDialog}
  onClose={() => setOpenPasswordDialog(false)}
  maxWidth="xs"
  fullWidth
>
  <DialogTitle>Change Password</DialogTitle>
 <DialogContent>
  <TextField
    fullWidth
    label="Current Password"
    type={showCurrent ? "text" : "password"}
    margin="normal"
    {...registerPassword("currentPassword")}
    error={!!passwordErrors.currentPassword}
    helperText={passwordErrors.currentPassword?.message}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <IconButton onClick={() => setShowCurrent(!showCurrent)}>
            {showCurrent ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    }}
  />

  <TextField
    fullWidth
    label="New Password"
    type={showNew ? "text" : "password"}
    margin="normal"
    {...registerPassword("newPassword")}
    error={!!passwordErrors.newPassword}
    helperText={passwordErrors.newPassword?.message}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <IconButton onClick={() => setShowNew(!showNew)}>
            {showNew ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    }}
  />

  <LinearProgress
    variant="determinate"
    value={(passwordStrength.score / 5) * 100}
    sx={{
      height: 6,
      borderRadius: 1,
      my: 1,
      backgroundColor: "#eee",
      "& .MuiLinearProgress-bar": {
        backgroundColor: passwordStrength.color,
      },
    }}
  />

  <TextField
    fullWidth
    label="Confirm New Password"
    type={showConfirmNew ? "text" : "password"}
    margin="normal"
    {...registerPassword("confirmNewPassword")}
    error={!!passwordErrors.confirmNewPassword}
    helperText={passwordErrors.confirmNewPassword?.message}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <IconButton onClick={() => setShowConfirmNew(!showConfirmNew)}>
            {showConfirmNew ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    }}
  />
</DialogContent>

  <DialogActions>
    <Button onClick={() => setOpenPasswordDialog(false)}>
      Cancel
    </Button>

    <Button
      variant="contained"
      onClick={handlePasswordSubmit(handlePasswordChange)}
    >
      Update
    </Button>
  </DialogActions>
</Dialog>
{/* <Snackbar
  open={snackbarOpen}
  autoHideDuration={3000}
  onClose={() => setSnackbarOpen(false)}
>
  <Alert severity="success" sx={{ width: "100%" }}>
    Password updated successfully
  </Alert>
</Snackbar> */}
      </Paper>
    </Box>
  );
};

export default VendorRegistration;
